import type { ReactNode } from 'react';
import type { SelectOption } from '@/shared/ui/select';

/**
 * Public types for the data table surface.
 *
 * This is a SERVER-DRIVEN table. The component never filters, sorts or
 * paginates a row set locally: it holds the query the server should answer,
 * hands that query to `onFetch`, and renders whatever rows come back. Every
 * row count on screen is a page, never the whole collection.
 *
 * That constraint is why `onFetch` is required and why `pageCount` comes from
 * `total` rather than from the rows. A table that sorted client-side would
 * look correct over three rows and silently truncate at three hundred.
 *
 * Type aliases rather than interfaces: the folder's interface guardrail exists
 * to stop hand-written API payload types, and these are props aliases that
 * already carry the right suffixes.
 */

/** The minimum shape a row must have to be keyed and rendered. */
export type DataTableRow = Record<string, unknown>;

/** What a custom cell renderer is given. */
export type DataTableCellContext<TData extends DataTableRow> = {
  /** The row the cell belongs to. */
  row: TData;
  /** The value read from `accessorKey`. */
  value: unknown;
  /**
   * Where the cell is being rendered.
   *
   * `'table'` and `'card'` are the two presentations. `'sheet'` is the bottom
   * sheet a card opens on a phone, where the cell has the full width of the
   * screen and no hover — so an actions cell can render a labelled row instead
   * of an icon-only square, and a tooltip-only label becomes a visible one.
   *
   * Optional and additive: a renderer that ignores it keeps working, and one
   * that reads it can adapt without the table knowing what the cell contains.
   */
  variant?: 'table' | 'card' | 'sheet';
};

/**
 * A column-bound filter control. The table renders one input per descriptor,
 * stores the value in its own state, and ships it as `DataTableParams.filters[<id>]`.
 *
 * The descriptor is what the user interacts with; the value is the search
 * term or selected option keyed by `column.id`. A column with no `filter` is
 * not filtered, and the table does not allocate state for it.
 */
export type DataTableColumnFilter =
  | {
      type: 'text';
      placeholder?: string;
    }
  | {
      type: 'select';
      options: SelectOption[];
      placeholder?: string;
    };

/** A column descriptor. This is the whole declarative surface — not markup. */
export type DataTableColumn<TData extends DataTableRow = DataTableRow> = {
  /** Column identity, and the sort key sent to the server. */
  id: string;
  /** Visible header text. */
  header: string;
  /** Property read when no `cell` renderer is supplied. */
  accessorKey?: keyof TData & string;
  /** Custom renderer. Omit to render the raw value as text. */
  cell?: (context: DataTableCellContext<TData>) => ReactNode;
  /** Whether the server can sort by this column. */
  sortable?: boolean;
  /**
   * Whether the first click on a sortable column asks for descending. Defaults
   * to TanStack's inference from the cell value; pass false for a column whose
   * value the table cannot infer (a custom cell) that must start ascending.
   */
  sortDescFirst?: boolean;
  /** Whether the user may hide this column. Defaults to true. */
  hideable?: boolean;
  /**
   * Whether this column identifies a row.
   *
   * In the card presentation the primary column becomes the card's heading.
   * Without it the first column is used, which is only a guess: on a page whose
   * first column is an action the card would be titled with a bare value and
   * the record it describes would be nowhere on the card. At most one column
   * should set this; the first one wins if several do.
   */
  primary?: boolean;
  align?: 'left' | 'center' | 'right';
  /** Pin the column to an edge so it stays visible during horizontal scroll. */
  sticky?: 'left' | 'right';
  /**
   * A column-bound filter control.
   *
   * When set, the table renders the matching input in the filter bar and ships
   * its current value to the server in `DataTableParams.filters[<id>]`. The
   * id is shared with the column, so a `filter` descriptor doubles as the
   * filter key — there is no separate "filter id" to keep in sync.
   */
  filter?: DataTableColumnFilter;
};

/** One sort term, in the shape a server query expects. */
export type DataTableSort = {
  /** Column id being sorted. */
  id: string;
  /** Descending when true. */
  desc: boolean;
};

/** The complete query the table wants the server to answer. */
export type DataTableParams = {
  /** 1-based page number. */
  page: number;
  pageSize: number;
  /** Active sort terms, in precedence order. */
  sort: DataTableSort[];
  /**
   * Active filters, including the free-text search under `search` and any
   * column-bound filter values keyed by column id.
   */
  filters: Record<string, string>;
  /**
   * Whether the rows for this page CONTINUE the list already on screen rather
   * than replace it.
   *
   * The two presentations move through the same collection differently. Table
   * view has a pager: clicking "Next page" asks for a different page of the
   * same list and the previous page leaves the screen, so the answer replaces.
   * Card view has no pager — it loads the next page when the sentinel scrolls
   * into view — and a grid that replaced its rows on every scroll would show
   * only the newest page, so the answer appends.
   *
   * The table states which one it is asking for; the caller decides what to do
   * with it. A caller that ignores the flag keeps the replace behaviour it has
   * always had, which is why this is optional.
   */
  append?: boolean;
};

/**
 * Where the table is in its data lifecycle.
 *
 * `loading` and `error` are distinct from `empty` on purpose: "we are still
 * asking" and "there is nothing here" demand different screens, and a table
 * that shows an empty state while loading tells the user their filter matched
 * nothing.
 */
export type DataTableStatus = 'loading' | 'error' | 'ready' | 'empty';

/** A saved view rendered as a tab. Selecting one applies its filters. */
export type DataTableView = {
  id: string;
  label: string;
  /** Filters applied when this view is selected. */
  filters?: Record<string, string>;
  /**
   * Count rendered as a badge next to the tab label.
   *
   * Optional: not every view knows its count ahead of time, and a tab strip
   * with no badges is the same surface. When provided, the table renders the
   * count and leaves its source — server, store, derived — to the caller.
   */
  count?: number;
};

/** Default page-size options for the per-page selector in the footer. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export type DataTableProps<TData extends DataTableRow = DataTableRow> = {
  /** Column descriptors. */
  columns: DataTableColumn<TData>[];
  /** The current page of rows. The server owns them. */
  data: TData[];
  /** Total matching rows across all pages, not the length of `data`. */
  total: number;
  /** Data lifecycle state. */
  status: DataTableStatus;
  /**
   * The only data path. Called on mount and whenever the page, sort or filters
   * change. Keep this reference stable — an unstable identity re-queries on
   * every render.
   */
  onFetch: (params: DataTableParams) => void;
  /** Stable identity for a row. Required: selection and React keys use it. */
  rowKey: (row: TData) => string;
  /** Controlled page number, 1-based. Omit for an uncontrolled table. */
  page?: number;
  /** Called when the page changes. */
  onPageChange?: (page: number) => void;
  defaultPage?: number;
  defaultPageSize?: number;
  /**
   * Controlled page size. Omit to let the footer's per-page selector own it,
   * starting at `defaultPageSize` — the chosen size drives the query.
   */
  pageSize?: number;
  /** Called when the page size changes from the footer selector. */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Options for the per-page selector. Defaults to `[10, 25, 50, 100]`; pass
   * an empty array to hide the selector entirely.
   */
  pageSizeOptions?: number[];
  /** Free-text query. Sent as the `search` filter. */
  searchValue?: string;
  /** Called with the new free-text query as the user types. */
  onSearchChange?: (value: string) => void;
  /** Extra filters contributed by caller-supplied filter controls. */
  filters?: Record<string, string>;
  /**
   * Called with the column-bound filter values whenever one changes.
   *
   * A page whose read can be narrowed by the server uses this to put the filter in
   * the request; a page whose read cannot be narrowed omits it, and the filter
   * narrows the rows already on screen.
   */
  onColumnFiltersChange?: (filters: Record<string, string>) => void;
  /** Renders the row-selection column and enables selection. */
  selectable?: boolean;
  /** Called with the keys of the selected rows. */
  onSelectionChange?: (rowKeys: string[]) => void;
  /** Saved views rendered as a tab strip. */
  views?: DataTableView[];
  /**
   * Controlled sort state.
   *
   * When supplied, the header sort indicators render from this list instead of
   * the table's internal sorting state — for a table whose sort is owned by the
   * caller (a URL query, a page's local query engine). Sort clicks are still
   * reported through `onFetch`; the caller answers by updating this prop.
   * Omit for an internally sorted table.
   */
  sort?: DataTableSort[];
  /**
   * Whether sortable columns render a clickable control. Default true. Set
   * false to announce the current sort (`aria-sort`) without offering a toggle
   * — for a sort the user cannot change from this table.
   */
  sortControls?: boolean;
  /** Which presentation to render. */
  presentation?: 'table' | 'cards';
  /** Accessible name of the table and its scroll region. */
  caption?: string;
  /**
   * Rendered as a real `<caption>` inside the `<table>` when supplied. Omit to
   * keep the accessible name on the scroll region only.
   */
  tableCaption?: ReactNode;
  /** Replaces infinite scrolling with explicit page controls. Default true. */
  paginated?: boolean;
  /**
   * Whether the footer may render at all. Default true. The footer still hides
   * itself for a single page with no paging callback; set false on a table with
   * no paging surface so an inert pager is never drawn.
   */
  showFooter?: boolean;
  /**
   * Whether the toolbar (search, column filters, column picker, presentation
   * toggle) renders. Default true. Set false for a table that opted into no
   * toolbar capability, so the toolbar's furniture does not appear above a
   * plain list.
   */
  toolbar?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /**
   * The copy for the table's own empty row, reached when the collection has rows
   * but the current tab, search or filter matched none of them.
   *
   * It is separate from `emptyTitle`/`emptyDescription` because those describe an
   * EMPTY COLLECTION and carry its action; this describes a query that matched
   * nothing, on a screen where the chrome that produced the query is still there.
   */
  noMatchTitle?: string;
  noMatchDescription?: string;
  /** Rendered as an alert when `status` is `error`. */
  errorMessage?: string;
  /** Compact pagination footer: chevrons + minimal page text, no range text. */
  compact?: boolean;
  /** Rendered in the bulk bar while rows are selected. */
  bulkActions?: ReactNode;
  className?: string;
};

/**
 * `DataTableProps` after the documented defaults have been applied.
 *
 * Folder-internal: it exists so the component body never has to carry a
 * default value in its destructuring. Every default is a branch, and a table
 * with eight of them is a function that fails the complexity budget while
 * doing nothing harder than the button component does.
 */
export type ResolvedDataTableProps<TData extends DataTableRow = DataTableRow> = DataTableProps<TData> & {
  defaultPage: number;
  defaultPageSize: number;
  pageSizeOptions: number[];
  presentation: 'table' | 'cards';
  caption: string;
  paginated: boolean;
  showFooter: boolean;
  toolbar: boolean;
  sortControls: boolean;
  errorMessage: string;
  compact: boolean;
  filters: Record<string, string>;
  selectable: boolean;
};
